import dayjs, { Dayjs } from 'utils/dayjs'
import cn from 'utils/cn'
import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'

const getWeeksInMonth = (year: number, month: number): Dayjs[][] => {
  const firstDayOfMonth: Dayjs = dayjs()
    .year(year)
    .month(month)
    .startOf('month')
  const lastDayOfMonth: Dayjs = dayjs().year(year).month(month).endOf('month')
  const weeks: Dayjs[][] = []

  let currentWeek: Dayjs[] = []
  let currentDay: Dayjs = firstDayOfMonth.clone().startOf('week')

  while (currentDay.isSameOrBefore(lastDayOfMonth, 'day')) {
    currentWeek.push(currentDay.clone())

    if (currentDay.weekday() === 6) {
      weeks.push(currentWeek)
      currentWeek = []
    }
    currentDay = currentDay.add(1, 'day')
  }

  if (currentWeek.length > 0) {
    weeks.push(currentWeek)
  }

  const lastWeekOfMonth = [...weeks[weeks.length - 1]]
  const daysToFillWeek = 7 - lastWeekOfMonth.length

  // Fill in missing days of the calendar week
  if (daysToFillWeek) {
    for (let i = 0; i < daysToFillWeek; i++) {
      lastWeekOfMonth.push(
        lastWeekOfMonth[lastWeekOfMonth.length - 1].clone().add(1, 'day')
      )
    }
    weeks[weeks.length - 1] = lastWeekOfMonth
  }
  return weeks
}

const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
  <div
    key={day}
    className="w-full h-8 flex items-center justify-start pl-1 text-dark-3 text-sm"
  >
    {day}
  </div>
))

interface CalendarProps {
  /**
   * The current date displayed in the calendar.
   */
  date: Dayjs
  /**
   * The selected date value. `Dayjs` object or `null` for no selection.
   */
  value?: Dayjs | null | undefined
  /**
   * Callback fired when a date is selected.
   */
  onChange?: (date: Dayjs) => void
  /**
   * The start date for range selection.
   */
  startDate?: Dayjs | null
  /**
   * The end date for range selection.
   */
  endDate?: Dayjs | null
  /**
   * Callback fired when the date range changes.
   */
  onChangeRange?: (dates: (Dayjs | null)[], shouldUpdate: boolean) => void
  /**
   * If true, enables range selection mode.
   */
  isRange?: boolean
  /**
   * If true, disables selection of past dates.
   */
  disablePast?: boolean
  /**
   * Disables selection of dates before this value.
   */
  disableBefore?: Dayjs | null
  /**
   * Disables selection of dates after this value.
   */
  disableAfter?: Dayjs | null
  /**
   * Optional predicate — when provided, a day is disabled if this returns true.
   * Applied in addition to `disableBefore`/`disableAfter`.
   */
  shouldDisableDate?: (d: Dayjs) => boolean
}

/**
 * Calendar component used internally on `DatePicker` and `RangePicker`
 * */
const Calendar = ({
  date,
  onChange,
  value,
  startDate,
  endDate,
  onChangeRange,
  isRange,
  disablePast,
  disableBefore,
  disableAfter,
  shouldDisableDate
}: CalendarProps) => {
  const weeksInMonth: Dayjs[][] = getWeeksInMonth(date.year(), date.month())
  const [step, setStep] = useState<'start' | 'end'>('start')

  const isDaySelected = useCallback(
    (day: Dayjs): boolean => {
      if (isRange) {
        return (
          dayjs(startDate as Dayjs).isSame(day, 'd') ||
          dayjs(endDate as Dayjs).isSame(day, 'd')
        )
      }

      if (value === null || value === undefined) {
        return false
      }

      if (dayjs(value as Dayjs).isValid()) {
        return dayjs(value).isSame(day, 'd')
      }

      return false
    },
    [endDate, isRange, startDate, value]
  )
  useEffect(() => {
    if (isRange && !startDate) {
      return setStep('start')
    }

    if (isRange && startDate && endDate) {
      return setStep('start')
    }

    if (isRange && startDate && !endDate) {
      return setStep('end')
    }

    if (!isRange) setStep('start')
  }, [endDate, isRange, startDate])

  const checkDisabled = useCallback(
    (day: Dayjs) => {
      if (isRange) {
        return step === 'end' || disablePast ? day.isBefore(startDate) : false
      }
      const shouldDisableBefore = disableBefore
        ? day.isBefore(disableBefore)
        : false
      const shouldDisableAfter = disableAfter
        ? day.isAfter(disableAfter)
        : false

      return (
        shouldDisableBefore ||
        shouldDisableAfter ||
        (shouldDisableDate?.(day) ?? false)
      )
    },
    [
      isRange,
      step,
      disablePast,
      startDate,
      disableBefore,
      disableAfter,
      shouldDisableDate
    ]
  )

  return (
    <div className="w-[180px] m-auto">
      <div className="flex flex-row gap-2">{weekDays}</div>
      {weeksInMonth.map((week: Dayjs[], index: number) => {
        return (
          <div key={index} className="flex flex-row">
            {week.map((day: Dayjs) => {
              const isToday = dayjs().isSame(day, 'day')
              const isDiffMonth = !dayjs(date).isSame(day, 'month')
              const isSelected = isDaySelected(day)
              const isDisabled = checkDisabled(day)

              const isBetween =
                startDate?.isValid() &&
                endDate?.isValid() &&
                dayjs(day).isBetween(startDate, endDate, 'days', '[]')

              const isLeftEdge =
                day.day() === 0 || startDate?.isSame(day, 'day')
              const isRightEdge = day.day() === 6 || endDate?.isSame(day, 'day')

              return (
                <div
                  key={day.format('YYYY-MM-DD')}
                  className="flex w-full items-center justify-around gap-1"
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn([
                      'w-full h-7 select-none flex items-center justify-around text-sm  border border-transparent rounded-md cursor-pointer hover:bg-dark-1 my-1',
                      isToday && 'bg-dark-1',
                      isDiffMonth && 'text-dark-2',
                      isDisabled &&
                        'text-dark-2 hover:bg-white! cursor-auto! border border-transparent',
                      (isSelected || isBetween) && 'border-primary',
                      isBetween && 'border-x-0! rounded-none',
                      isBetween && isLeftEdge && 'border-l! rounded-l-md ',
                      isRightEdge && isRightEdge && 'border-r! rounded-r-md '
                    ])}
                    onClick={() => {
                      if (isDisabled) return
                      if (isRange) {
                        if (step === 'start') {
                          onChangeRange?.([day, null], false)
                          setStep('end')
                        }
                        if (step === 'end') {
                          onChangeRange?.([startDate as Dayjs, day], true)
                          setStep('start')
                        }
                      }

                      onChange?.(day.clone())
                    }}
                  >
                    {day.format('D')}
                  </motion.div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default Calendar
